"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense } from "react";
import { countryGroups } from "@/data/countries";
import { regionShipping } from "@/data/shipping-carriers";
import { useLocale } from "@/lib/useLocale";
import { t } from "@/lib/translations";
import { LangToggle } from "@/components/LangToggle";

function ExportInquiryFormContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { locale } = useLocale();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    country: "",
    products: [] as string[],
    quantity: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const selectedRegion = Object.entries(countryGroups).find(([, countries]) =>
    countries.includes(formData.country)
  )?.[0];
  const shippingInfo = selectedRegion ? regionShipping[selectedRegion] : null;

  useEffect(() => {
    const initialProduct = searchParams.get("product");
    if (initialProduct) {
      setFormData((prev) => ({ ...prev, products: [initialProduct] }));
    }
  }, [searchParams]);

  const categoryLabels: Record<string, string> = {
    powder: t(locale, "product.powder"),
    fresh: t(locale, "product.fresh"),
    extract: t(locale, "product.extract"),
    bean: t(locale, "product.bean"),
    leaf: t(locale, "product.leaf"),
  };

  const categories = ["Powder", "Fresh Herbs", "Extracts", "Beans", "Leaves"];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProductChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { selectedOptions } = e.target;
    const selectedProducts = Array.from(selectedOptions).map((option) => option.value);
    setFormData((prev) => ({ ...prev, products: selectedProducts }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch("/api/export-inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit inquiry");
      }

      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        company: "",
        country: "",
        products: [],
        quantity: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      alert(t(locale, "form.submitError"));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-4 pt-24 pb-12">
        <div className="max-w-2xl mx-auto bg-brand-50 border border-brand-200 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-brand-800 mb-4">{t(locale, "submitted.title")}</h2>
          <p className="text-brand-700 mb-6">
            {t(locale, "submitted.body")}
          </p>
          <a
            href="/export-inquiry"
            className="inline-block bg-brand-600 text-white px-6 py-2 rounded-lg hover:bg-brand-700 transition-colors"
          >
            {t(locale, "button.submitAnother")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-24 pb-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold font-display text-brand-900 mb-2">{t(locale, "export.title")}</h1>
            <p className="text-gray-600">
              {t(locale, "export.subtitle")}
            </p>
          </div>
          <LangToggle className="shrink-0" />
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-8 border border-gray-100">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t(locale, "form.fullName")}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t(locale, "form.email")}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  {t(locale, "form.company")}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  {t(locale, "form.country")}
                </label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">{t(locale, "form.selectCountry")}</option>
                  {Object.entries(countryGroups).map(([region, regionCountries]) => (
                    <optgroup key={region} label={region}>
                      {regionCountries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {shippingInfo && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-xs font-semibold text-blue-800 mb-2">
                      {t(locale, "shipping.available", { country: formData.country })}
                    </p>
                    <div className="space-y-1.5">
                      {shippingInfo.carriers.map((opt) => (
                        <div key={opt.carrier} className="flex items-center gap-2 text-xs text-blue-700">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                            opt.type === "air" ? "bg-sky-400" : "bg-blue-500"
                          }`} />
                          <span className="font-medium">{opt.carrier}</span>
                          <span className="text-blue-400">·</span>
                          <span>{opt.estimate}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-blue-500 mt-1.5">
                      {shippingInfo.carriers.some(c => c.type === "sea")
                        ? t(locale, "shipping.seaAir")
                        : t(locale, "shipping.air")}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="products" className="block text-sm font-medium text-gray-700 mb-2">
                {t(locale, "form.products")}
              </label>
              <select
                id="products"
                name="products"
                value={formData.products}
                onChange={handleProductChange}
                multiple
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 h-32"
                required
              >
                {categories.map((category) => (
                  <optgroup key={category} label={category}>
                    <option value="powder">{t(locale, "product.powder")}</option>
                    <option value="fresh">{t(locale, "product.fresh")}</option>
                    <option value="extract">{t(locale, "product.extract")}</option>
                    <option value="bean">{t(locale, "product.bean")}</option>
                    <option value="leaf">{t(locale, "product.leaf")}</option>
                  </optgroup>
                ))}
              </select>
              <p className="text-sm text-gray-500 mt-1 hidden sm:block">
                {t(locale, "product.help.desktop")}
              </p>
              <p className="text-sm text-gray-500 mt-1 sm:hidden">
                {t(locale, "product.help.mobile")}
              </p>
            </div>

            <div>
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                {t(locale, "form.quantity")}
              </label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder={t(locale, "form.quantityPlaceholder")}
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                {t(locale, "form.message")}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder={t(locale, "form.messagePlaceholder")}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-lg font-medium transition-colors ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-brand-600 text-white hover:bg-brand-700"
              }`}
            >
              {loading ? t(locale, "button.submitting") : t(locale, "button.submit")}
            </button>
          </div>
        </form>

        <div className="mt-8 bg-brand-50 border border-brand-200 rounded-lg p-6">
          <h3 className="font-semibold text-brand-900 mb-2">{t(locale, "response.title")}</h3>
          <p className="text-brand-700 text-sm">
            {t(locale, "response.body")}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ExportInquiryForm() {
  return (
    <Suspense fallback={<div className="container mx-auto px-4 pt-24 pb-12 text-center">Loading...</div>}>
      <ExportInquiryFormContent />
    </Suspense>
  );
}
